import { Mail, MessageCircle } from "lucide-react";
import { Logo } from "@/components/shared/Logo";
import { LanguageToggle } from "@/components/shared/LanguageToggle";
import { useLanguage } from "@/context/LanguageContext";
import { CONTENT } from "@/i18n/content";
import { SUPPORT_EMAIL, getWhatsAppLink } from "@/lib/config";

const PRODUCT_TARGETS = ["sample", "how", "powers", "pricing", "faq"];

export function Footer() {
  const { lang } = useLanguage();
  const t = CONTENT[lang].footer;
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <footer className="bg-navy-dark text-cream" data-testid="footer">
      <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8">
        <div className="grid gap-10 lg:grid-cols-12">
          {/* Brand */}
          <div className="lg:col-span-4">
            <Logo variant="light" size="lg" />
            <p className="mt-5 max-w-xs text-sm leading-relaxed text-cream/60">{t.tagline}</p>
          </div>

          {/* Product */}
          <div className="lg:col-span-2">
            <h4 className="text-sm font-semibold text-gold">{t.columns.product.title}</h4>
            <ul className="mt-4 space-y-2.5">
              {t.columns.product.links.map((link, i) => (
                <li key={link}>
                  <button
                    onClick={() => scrollTo(PRODUCT_TARGETS[i])}
                    className="text-sm text-cream/65 transition-colors hover:text-cream"
                  >
                    {link}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div className="lg:col-span-2">
            <h4 className="text-sm font-semibold text-gold">{t.columns.company.title}</h4>
            <ul className="mt-4 space-y-2.5">
              {t.columns.company.links.map((link) => (
                <li key={link}>
                  <a href="#" className="text-sm text-cream/65 transition-colors hover:text-cream">{link}</a>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div className="lg:col-span-2">
            <h4 className="text-sm font-semibold text-gold">{t.columns.legal.title}</h4>
            <ul className="mt-4 space-y-2.5">
              {t.columns.legal.links.map((link) => (
                <li key={link}>
                  <a href="#" className="text-sm text-cream/65 transition-colors hover:text-cream">{link}</a>
                </li>
              ))}
            </ul>
          </div>

          {/* Support */}
          <div className="lg:col-span-2">
            <h4 className="text-sm font-semibold text-gold">{t.supportTitle}</h4>
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              data-testid="footer-email"
              className="mt-4 flex items-center gap-2 text-sm text-cream/65 transition-colors hover:text-cream"
            >
              <Mail className="h-4 w-4" /> {SUPPORT_EMAIL}
            </a>
            <a
              href={getWhatsAppLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-whatsapp"
              className="mt-3 flex items-center gap-2 text-sm text-cream/65 transition-colors hover:text-cream"
            >
              <MessageCircle className="h-4 w-4" /> {t.whatsapp}
            </a>
            <p className="mt-3 text-xs leading-relaxed text-cream/45">{t.supportHours}</p>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-center justify-between gap-6 border-t border-cream/10 pt-8 sm:flex-row">
          <p className="text-xs text-cream/50">© 2026 ShubhSamay. {t.rights}</p>
          <LanguageToggle testIdPrefix="footer-lang" className="border-cream/20 bg-white/90" />
        </div>
      </div>
    </footer>
  );
}
